import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useDispatch } from "react-redux";
import { Settings } from "../../settings";
import RoyalCollectOverlaySymbol from "./RoyalCollectOverlaySymbol";
import { reportRoyalCollectOverlayReelReady } from "../../State/actions";

/**
 * RoyalCollectOverlayReel
 *
 * Container for overlay symbols in Royal Collect.
 * Contains SYMBOLS_VISIBLE overlay symbols (one per row).
 * No reel controller — each symbol has its own controller.
 * Mirrors HoldAndWinOverlayReel pattern.
 */
const RoyalCollectOverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing }, ref) => {
    const dispatch = useDispatch();
    const overlaySymbolRefs = useRef(new Array(Settings.SYMBOLS_VISIBLE));

    // Report this overlay reel as ready once mounted
    useEffect(() => {
      dispatch(reportRoyalCollectOverlayReelReady());
    }, [dispatch]);

    // Expose method to get symbol ref by index
    useImperativeHandle(ref, () => ({
      getSymbolRef: (symbolIndex) => overlaySymbolRefs.current[symbolIndex],
    }));

    const overlaySymbols = useMemo(
      () => Array.from({ length: Settings.SYMBOLS_VISIBLE }, (_, idx) => idx),
      [],
    );

    const containerX = useMemo(
      () => reelSpacing * (index - (Settings.REEL_COUNT - 1) / 2),
      [reelSpacing, index],
    );

    return (
      <Container name={`rc_overlay_reel_${index}`} x={containerX}>
        {overlaySymbols.map((_, idx) => (
          <RoyalCollectOverlaySymbol
            key={idx}
            index={idx}
            reelIndex={index}
            y={symbolHeight * idx}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            ref={(el) => {
              overlaySymbolRefs.current[idx] = el;
            }}
          />
        ))}
      </Container>
    );
  },
);

RoyalCollectOverlayReel.displayName = "RoyalCollectOverlayReel";

export default RoyalCollectOverlayReel;
